import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Paper,
  Skeleton,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import theme from "../../theme/theme";
import customerLabel from "../../utils/customerLabel";
import { fetchCustomerPage } from "../../api/CustomerService";
import AsyncAutocomplete from "../common/AsyncAutocomplete";

const emptyItem = {
  description: "",
  hsn_sac_code: "",
  quantity: 1,
  unit_price: 0,
  total_price: 0,
  cgst: 0,
  sgst: 0,
  total_amount: 0,
};

const EditInvoiceDialog = ({
  open,
  onClose,
  saving,
  loading,
  onSave,
  errors = {},
  customers = [],
  invoice,
}) => {
  const [form, setForm] = useState({
    customer_id: "",
    invoice_number: "",
    invoice_date: "",
    amount: "",
    items: [{ ...emptyItem }],
  });

  useEffect(() => {
    if (invoice && open) {
      setForm({
        customer_id: invoice.customer_id || "",
        invoice_number: invoice.invoice_number || "",
        invoice_date: invoice.invoice_date
          ? invoice.invoice_date.split("T")[0]
          : "",
        amount: invoice.amount ?? "",
        items:
          invoice.items?.length > 0
            ? invoice.items.map((it) => ({
                description: it.description || "",
                hsn_sac_code: it.hsn_sac_code || "",
                quantity: it.quantity || 1,
                unit_price: parseFloat(it.unit_price) || 0,
                total_price: parseFloat(it.total_price) || 0,
                cgst: parseFloat(it.cgst) || 0,
                sgst: parseFloat(it.sgst) || 0,
                total_amount: parseFloat(it.total_amount) || 0,
              }))
            : [{ ...emptyItem }],
      });
    }
  }, [invoice, open]);

  const recalcItem = (item) => {
    const totalPrice = item.quantity * item.unit_price;
    const cgst = totalPrice * 0.09;
    const sgst = totalPrice * 0.09;
    return {
      ...item,
      total_price: parseFloat(totalPrice.toFixed(2)),
      cgst: parseFloat(cgst.toFixed(2)),
      sgst: parseFloat(sgst.toFixed(2)),
      total_amount: parseFloat((totalPrice + cgst + sgst).toFixed(2)),
    };
  };

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    if (["quantity", "unit_price"].includes(field)) {
      items[index] = recalcItem(items[index]);
    }
    setForm({ ...form, items });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, { ...emptyItem }] });
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const totals = form.items.reduce(
    (acc, item) => ({
      invoiceAmmount: acc.invoiceAmmount + (item.total_price || 0),
      cgst: acc.cgst + (item.cgst || 0),
      sgst: acc.sgst + (item.sgst || 0),
    }),
    { invoiceAmmount: 0, cgst: 0, sgst: 0 },
  );
  const totalInvoiceAmount = totals.invoiceAmmount + totals.cgst + totals.sgst;
  const totalAmount = (parseFloat(form.amount) || 0) + totalInvoiceAmount;

  const handleSave = () => {
    onSave({
      customer_id: form.customer_id,
      invoice_number: form.invoice_number.trim(),
      invoice_date: form.invoice_date,
      amount: parseFloat(form.amount) || 0,
      cgst: parseFloat(totals.cgst.toFixed(2)),
      sgst: parseFloat(totals.sgst.toFixed(2)),
      invoice_ammount: parseFloat(totals.invoiceAmmount.toFixed(2)),
      items: form.items,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.dialog.borderRadius, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        Edit Invoice {invoice?.invoice_number ? `— ${invoice.invoice_number}` : ""}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box>
            <Skeleton height={56} sx={{ mb: 2 }} />
            <Skeleton height={200} />
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
              <AsyncAutocomplete
                label="Customer"
                placeholder="Search customer..."
                value={form.customer_id}
                onChange={(id) => setForm({ ...form, customer_id: id })}
                fetchPage={fetchCustomerPage}
                getOptionLabel={customerLabel}
                selectedOption={
                  (invoice?.customer?.id === form.customer_id
                    ? invoice.customer
                    : null) ||
                  customers.find((c) => c.id === form.customer_id) ||
                  null
                }
                error={!!errors.customer_id}
                helperText={errors.customer_id?.[0]}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Invoice Number"
                value={form.invoice_number}
                onChange={(e) =>
                  setForm({ ...form, invoice_number: e.target.value })
                }
                error={!!errors.invoice_number}
                helperText={errors.invoice_number?.[0]}
                sx={{ width: 220 }}
              />
              <TextField
                size="small"
                label="Invoice Date"
                type="date"
                value={form.invoice_date}
                onChange={(e) =>
                  setForm({ ...form, invoice_date: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                error={!!errors.invoice_date}
                helperText={errors.invoice_date?.[0]}
                sx={{ width: 200 }}
              />
              <TextField
                size="small"
                label="Amount"
                type="number"
                value={form.amount}
                onChange={(e) =>
                  setForm({ ...form, amount: e.target.value })
                }
                error={!!errors.amount}
                helperText={errors.amount?.[0]}
                inputProps={{ min: 0 }}
                sx={{ width: 200 }}
              />
            </Box>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Invoice Items
            </Typography>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 700 }}>Sr. No</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>HSN/SAC</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 80 }}>Qty</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 110 }}>
                      Unit Price
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Total
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      CGST
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      SGST
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      Amount
                    </TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.items.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          fullWidth
                          value={item.description}
                          onChange={(e) =>
                            updateItem(i, "description", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          fullWidth
                          value={item.hsn_sac_code}
                          onChange={(e) =>
                            updateItem(i, "hsn_sac_code", e.target.value)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          type="number"
                          value={item.quantity}
                          onChange={(e) =>
                            updateItem(
                              i,
                              "quantity",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          inputProps={{ min: 1 }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          variant="standard"
                          type="number"
                          value={item.unit_price}
                          onChange={(e) =>
                            updateItem(
                              i,
                              "unit_price",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        ₹{item.total_price.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ₹{item.cgst.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ₹{item.sgst.toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        ₹{item.total_amount.toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Remove Item" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeItem(i)}
                            disabled={form.items.length <= 1}
                            aria-label="Remove Item"
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              size="small"
              startIcon={<Add />}
              onClick={addItem}
              sx={{ textTransform: "none", mb: 2 }}
            >
              Add Item
            </Button>

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box sx={{ width: 350 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">Invoice Amount:</Typography>
                  <Typography variant="body2">
                    ₹{totals.invoiceAmmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">CGST:</Typography>
                  <Typography variant="body2">
                    ₹{totals.cgst.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">SGST:</Typography>
                  <Typography variant="body2">
                    ₹{totals.sgst.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                    borderTop: "1px solid",
                    borderColor: "divider",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    Total Invoice Amount:
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ₹{totalInvoiceAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2">Amount:</Typography>
                  <Typography variant="body2">
                    ₹{(parseFloat(form.amount) || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 1,
                    borderTop: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Total Amount:
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    ₹{totalAmount.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || loading}
          variant="contained"
          startIcon={<Save />}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditInvoiceDialog;
